import React, { useState, useEffect } from 'react';
import Highcharts from 'highcharts';
import expandIcon from '../assets/images/expand.svg'; // Adjust the path as necessary
import collapseIcon from '../assets/images/collapseImg.svg'; // Adjust the path as necessary
import StockChart from './StockChart';
import AreaRangeChart from './AreaRangeChart';

const StationGraph = ({ stationData, selectedStation, loading, dataType }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  useEffect(() => {
    if (isExpanded && dataType !== 'historic') {
      // getPlotChart(); // Re-initialize chart when expanded
      getPlotChartZoom(); // Re-initialize chart when expanded
    }
  }, [isExpanded, stationData]); // Depend on `isExpanded` and `stationData`

  const toggleContent = () => {
    setIsExpanded(!isExpanded);
  };

  const getLegendColors = () => {
    let return_periods =
      stationData.forecast2?.return_periods ||
      stationData.forecast1?.return_periods ||
      stationData.historic?.return_periods;

    if (!return_periods) return null; // Return nothing if no return periods exist

    if (dataType === 'forecast') {
      return_periods = [...return_periods, { label: "Normal Range", color: "lightblue" }];
    }

    return (
      <div className="d-flex flex-wrap">
        {return_periods.map((item, index) => (
          <div key={index} className="d-flex align-items-center me-3">
            <div
              className="legend-box"
              style={{
                width: "16px",
                height: "16px",
                borderRadius: 4,
                backgroundColor: item.color,
                marginRight: "5px",
                border: "1px solid rgba(0, 0, 0, 0.10)",
              }}
            ></div>
            <span style={{ fontSize: "12px", fontWeight: '500' }}>{item.label ?? `Flood Level ${index + 1}`}</span>
          </div>
        ))}
      </div>
    );
  };

  // Function to process forecast data into the proper format for Highcharts
  const processForecastData = (forecast) => {
    return forecast.map(item => ({
      x: new Date(item.date).getTime(), // Converts date string to timestamp
      y: item.flow_avg !== undefined
        ? Math.round(item.flow_avg * 100) / 100  // Historic mode (avg flow)
        : item.flow ? Math.round(item.flow * 100) / 100 : null    // Forecast mode (actual flow)
    }));
  };

  // Function to process return_periods data for plot bands and labels
  const processReturnPeriodData = (returnPeriod) => {
    return returnPeriod.reduce((acc, item, index) => {
      const currentFlow = item.flow ?? null; // Get the current flow value, or null if undefined
      const previousFlow = index === 0 ? 0 : returnPeriod[index - 1].flow ?? null; // Get the previous flow value, or null if undefined

      // Only process if both the current and previous flow are not null and if the current flow is different from the previous flow
      if (currentFlow !== null && currentFlow !== previousFlow) {
        acc.push({
          from: previousFlow ?? 0, // Use the previous flow as the "from" value
          to: currentFlow, // The "to" value is the current flow
          color: item.color
        });
      }

      return acc;
    }, []);
  };

  const getPlotChart = () => {
    if (!stationData && !stationData?.forecast1) return;
    let station_data = stationData.forecast1

    const returnPeriodData = processReturnPeriodData(station_data.return_periods);

    // Find min and max flow values from return periods
    const flowValues = station_data.return_periods.map(item => item.flow ?? 0);
    const minFlow = Math.min(...flowValues, 0); // Include 0 as a fallback
    const maxFlow = Math.max(...flowValues);

    let verticalLineDate, xAxisType, xAxisFormatter, plotLines = [];

    verticalLineDate = new Date().getTime();
    xAxisType = 'datetime';
    xAxisFormatter = function () {
      return Highcharts.dateFormat('%b %e', this.value) + '<br/>' + Highcharts.dateFormat('%Y', this.value);
    };
    // Add vertical line only if dataType is not "historic"
    if (dataType !== "historic") {
      plotLines.push({
        value: verticalLineDate,
        color: 'black',
        width: 1,
        zIndex: 10,
        label: {
          align: 'left',
          verticalAlign: 'bottom',
          style: { color: 'red' }
        }
      });
    }

    const chart = Highcharts.chart('station-graph', {
      chart: {
        type: 'spline',
        height: 300, // Set fixed height
        scrollablePlotArea: {
          minWidth: 600,
          scrollPositionX: 1
        }
      },
      title: {
        text: 'Flood range',
        align: 'left',
        style: {
          fontSize: '14px',
          fontWeight: '500',
          color: '#105586',
        }
      },
      xAxis: {
        type: xAxisType,
        title: { text: '' },
        labels: { formatter: xAxisFormatter },
        plotLines: plotLines
      },
      yAxis: {
        title: { text: 'Flow (m³/s)' },
        min: minFlow,
        max: maxFlow,
        startOnTick: false,
        endOnTick: false,
        softMin: minFlow - 5,
        softMax: maxFlow + 5,
        labels: {
          useHTML: true,
          style: {
            fontWeight: 'bold',
            background: 'rgba(255, 255, 255, 0.7)',
            padding: '2px',
            borderRadius: '3px'
          }
        },
        gridLineWidth: 0,
        plotBands: returnPeriodData
      },
      tooltip: {
        shared: true,
        useHTML: true,
        formatter: function () {
          const formattedX = Highcharts.dateFormat('%b %d, %Y', this.x);  // Format timestamp
          return `<b>${formattedX}</b><br>
            <span style="color:${this.series.color}">\u25CF</span> 
            ${this.series.name}: <b>${this.y.toFixed(2)} m³/s</b>`;
        },
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderColor: '#ccc',
        borderRadius: 5,
        shadow: true
      },
      plotOptions: {
        spline: {
          lineWidth: 2,
          marker: {
            enabled: false,
            radius: 3
          },
          boostThreshold: 1,
          jitter: { x: 0.01 },
          pointInterval: 86400000 // One day in milliseconds
        }
      },
      legend: {
        enabled: false
      },
      credits: { enabled: false },
      series: [
        {
          name: 'Forecast Flow',
          data: processForecastData(dataType === "historic" ? station_data.historic_data : station_data.forecast),
          color: '#3125be',
          boostThreshold: 1000
        }
      ],
      exporting: {
        enabled: false,
      },
      navigation: {
        menuItemStyle: { fontSize: '10px' }
      }
    });

    return () => {
      chart.destroy();
    };
  };

  const getPlotChartZoom = () => {
    if (!stationData && !stationData?.forecast1) return;

    let station_data = stationData.forecast1
    const returnPeriodData = processReturnPeriodData(station_data.return_periods);

    let verticalLineDate, xAxisType, xAxisFormatter, plotLines = [];

    verticalLineDate = Date.UTC(
      new Date().getUTCFullYear(),
      new Date().getUTCMonth(),
      new Date().getUTCDate()
    );
    xAxisType = 'datetime';
    xAxisFormatter = function () {
      return Highcharts.dateFormat('%b %e', this.value) + '<br/>' + Highcharts.dateFormat('%Y', this.value);
    };
    // Add vertical line only if dataType is not "historic"
    if (dataType !== "historic") {
      plotLines.push({
        value: verticalLineDate,
        color: 'black',
        width: 1,
        zIndex: 10,
        label: {
          align: 'left',
          verticalAlign: 'bottom',
          style: { color: 'red' }
        }
      });
    }

    const chart = Highcharts.chart('station-graph-zoom', {
      chart: {
        type: 'spline',
        height: 200, // Set fixed height
        scrollablePlotArea: {
          minWidth: 600,
          scrollPositionX: 1
        }
      },
      title: {
        text: 'Current forecast',
        align: 'left',
        style: {
          fontSize: '14px',
          fontWeight: '500',
          color: '#105586'
        }
      },
      credits: { enabled: false },

      xAxis: {
        type: xAxisType,
        title: { text: '' },
        labels: { formatter: xAxisFormatter },
        plotLines: plotLines
      },
      yAxis: {
        title: { text: 'Flow (m³/s)' },
        gridLineWidth: 0, // Hide grid lines
        plotBands: returnPeriodData || [] // Avoids undefined
      },
      tooltip: {
        shared: true,
        useHTML: true,
        formatter: function () {
          const formattedX = Highcharts.dateFormat('%b %d, %Y', this.x);  // Format timestamp
          return `<b>${formattedX}</b><br>
            <span style="color:${this.series.color}">\u25CF</span> 
            ${this.series.name}: <b>${this.y.toFixed(2)} m³/s</b>`;
        },
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderColor: '#ccc',
        borderRadius: 5,
        shadow: true
      },
      plotOptions: {
        spline: {
          lineWidth: 2,
          marker: { enabled: false },
          pointInterval: 86400000 // One day in milliseconds
        }
      },
      series: [
        {
          name: 'Forecast Flow',
          data: processForecastData(
            dataType === "historic" ? station_data.historic_data : station_data.forecast
          ),
          color: '#3125be'
        }
      ],
      legend: {
        enabled: false
      },
      exporting: {
        enabled: false,
      },
      navigation: {
        menuItemStyle: { fontSize: '10px' }
      }
    });

    return () => {
      chart.destroy();
    };
  };

  useEffect(() => {
    // Ensure the chart is created when the component mounts
    getPlotChartZoom();
  }, []); // Empty dependency array ensures this runs once on mount

  return (
    <>
      <div className="card legendCard three border-0" style={{ width: '630px', bottom: !isExpanded ? '75%' : '3px' }}>
        <div className="card-header px-2 pt-0 border-0 d-flex mb-0 align-items-center">
          <h5 className="legend-title f-12px pt-2 pb-0 mb-0">
            Station: ({selectedStation.properties.station} - {selectedStation.properties.name?.replace(/_/g, ' ')})
            {dataType === 'forecast' ? (
              <> / Date: {new Date().toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'long',
                year: 'numeric'
              })}
              </>
            ) : ''}
          </h5>
          <div>
            <button onClick={toggleContent} className="toggle-button" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
              <img
                src={isExpanded ? collapseIcon : expandIcon}
                alt={isExpanded ? 'Collapse' : 'Expand'}
                className="toggle-icon"
              />
            </button>
          </div>
        </div>
        {isExpanded && (
          <div className="card-body px-0 bg-light pt-2 pb-0 mb-0">
            <div className="mx-2">
              {loading ? (
                <div className="loader-container" style={{ height: '530px' }}>
                  <div className="spinner-border text-primary" role="status">
                    <span className="sr-only"></span>
                  </div>
                </div>
              ) : (
                stationData ? (
                  <>
                    <div className="bg-white mb-2 p-3">{getLegendColors()}</div>
                    {dataType !== 'historic' && <div className="d-flex flex-wrap legend-text mb-0 pb-0" id="station-graph"></div>}
                    {dataType === 'historic' && <StockChart chartData={stationData} />}
                    <div className="d-flex flex-wrap legend-text mb-2 pb-0" id="station-graph-zoom"></div>
                    {selectedStation && dataType === 'forecast' && (
                      <>
                        <AreaRangeChart
                          key={`${selectedStation?.properties?.station}-${dataType}-forecast2`}
                          stationData={stationData}
                          selectedStation={selectedStation}
                          loading={loading}
                          dataType={dataType}
                        />
                      </>
                    )}
                  </>
                ) : (
                  <div className="d-flex flex-wrap legend-text mb-0 pb-0 text-danger">
                    Data is not available for this station
                  </div>
                )
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default StationGraph;